import * as React from 'react'
import { useSession } from 'next-auth/react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'

import PageLayoutContainer from '../../components/shared/page-layout/PageLayoutContainer'
import ProductsList from '../../components/webshop/products-list/products-list'
import ProductDetail from '../../components/webshop/product-detail/ProductDetail'
import { fetchPackages } from '../../state/actions/webshop/webshop'
import fetchAndSetEntitlements from '../../components/products/util/fetch-and-set-entitlements/fetchAndSetEntitlements'
import { usePageLayout } from '../../util/usePageLayout/usePageLayout'
import { useAppSelector } from '../../state/reducers/types'
import { WebshopPackage } from '../../schema/webshop-package'
import InfoCard, { Variants } from '../../stories/atoms/InfoCard/InfoCard'
import InformationSvg from '../../stories/atoms/Icons/General/Information'
import breakpoints from '../../stories/theme/breakpoints'
import { selectOtherProduct } from '../../state/selectors/webshop/webshop'

const HIDDEN_PRODUCT_KEY = 'hidden-product'

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 45px;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 16px;
    width: 100%;
    padding: 16px;
  }
`

const Webshop: React.FunctionComponent = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const session = useSession()
  const normalProducts: WebshopPackage[] = useAppSelector(state => state.webshop.packages)
  const hiddenProducts: WebshopPackage[] = useAppSelector(state => state.webshop.hiddenPackages)
  let productList = [...normalProducts]

  const visitingProductCode = router.query.id as string
  const normalProduct = normalProducts.find(({ productCode }) => productCode === visitingProductCode)
  const hiddenProduct = hiddenProducts.find(({ productCode }) => productCode === visitingProductCode)  
  const visitingProduct = normalProduct || hiddenProduct

  const showProductList = !visitingProductCode
  const showProductDetail = !showProductList && !!visitingProduct

  if (hiddenProduct && typeof sessionStorage !== 'undefined') {
    // Store hidden product id in session storage
    sessionStorage.setItem(HIDDEN_PRODUCT_KEY, visitingProductCode)
  }

  if (showProductList && typeof sessionStorage !== 'undefined') {
    // For a user that visited a hidden product show hidden product
    // in current product list in current user session
    const visitedhiddenProductKey = sessionStorage.getItem(HIDDEN_PRODUCT_KEY)
    const visitedHiddenProduct = hiddenProducts.find(({ productCode }) => productCode === visitedhiddenProductKey)
    if (visitedHiddenProduct) {
      productList = [visitedHiddenProduct, ...normalProducts]
    }
  }

  const otherProduct = useAppSelector(selectOtherProduct(visitingProductCode))

  const pageLayoutProps = usePageLayout()

  React.useEffect(() => {
    dispatch(fetchPackages())
  }, [])

  React.useEffect(() => {
    if (session.status === 'authenticated') {
      fetchAndSetEntitlements(dispatch)
    }
  }, [session.status])

  const isAuthenticated = (session.status === 'authenticated')

  if (Array.isArray(router.query.pcodes)) {
    return (
      <PageLayoutContainer
        title="Webshop"
        withNavigation
        reportRejectedJobs={isAuthenticated}
        {...pageLayoutProps}
        shouldFetchRecruiterInfo={isAuthenticated}
        hideDialog={true}
      >
        <Container>
          <InfoCard
            icon={<InformationSvg />}
            title="Warning"
            variant={Variants.Purple}
            data-testid="parameterWarning"
          >
            Parameter "pcodes" meerdere keren gebruikt!<br />
            Gebruik het in plaats daarvan zoals: pcodes=product1, product2
          </InfoCard>
        </ Container>
      </PageLayoutContainer>
    )
  }

  const targetProductCodes = router.query.pcodes ? (router.query.pcodes as string).split(',') : []

  return (
    <PageLayoutContainer
      title="Webshop"
      withNavigation
      reportRejectedJobs={isAuthenticated}
      {...pageLayoutProps}
      shouldFetchRecruiterInfo={isAuthenticated}
      hideDialog={true}
    >
      <Container>
        {showProductList && <ProductsList products={productList} targetProductCodes={targetProductCodes} />}
        {showProductDetail && <ProductDetail product={visitingProduct} otherProduct={otherProduct} />}
      </Container>
    </PageLayoutContainer>
  )
}

export default Webshop
